import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  FolderKanban,
  CreditCard,
  TrendingUp,
  Clock,
  ArrowRight,
  MessageSquare,
  Users,
  Settings,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Badge, statusBadgeVariants } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { useAuth } from '../../contexts/AuthContext';
import { getProjects, getPayments, getInquiries } from '../../lib/supabase/queries';
import type { Project, Payment, Inquiry } from '../../types';
import { format } from 'date-fns';

export function DashboardOverview() {
  const { profile, isAdmin } = useAuth();
  const [projects, setProjects] = useState<Project[]>([]);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [inquiries, setInquiries] = useState<Inquiry[]>([]);

  useEffect(() => {
    let cancelled = false;

    async function fetchData() {
      try {
        // Fetch data - for non-admins, filter by profile ID if available
        const clientId = isAdmin ? undefined : profile?.id;

        const [projectsData, paymentsData] = await Promise.all([
          getProjects(clientId),
          getPayments(clientId),
        ]);

        if (cancelled) return;

        setProjects(projectsData);
        setPayments(paymentsData);

        if (isAdmin) {
          const inquiriesData = await getInquiries();
          if (!cancelled) {
            setInquiries(inquiriesData);
          }
        }
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      }
    }

    fetchData();

    return () => {
      cancelled = true;
    };
  }, [profile?.id, isAdmin]);

  // Calculate stats
  const activeProjects = projects.filter(
    (p) => p.status !== 'complete'
  ).length;
  const pendingPayments = payments.filter((p) => p.status === 'pending');
  const totalPending = pendingPayments.reduce((sum, p) => sum + p.amount, 0);
  const newInquiries = inquiries.filter((i) => i.status === 'new').length;

  const recentProjects = projects.slice(0, 3);
  const recentPayments = payments.slice(0, 5);

  return (
    <div className="space-y-6">
      {/* Welcome Header */}
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-obsidian dark:text-signal">
          Welcome back{profile?.company_name ? `, ${profile.company_name}` : ''}
        </h1>
        <p className="mt-1 text-slate-600 dark:text-slate-400">
          Here's what's happening with your {isAdmin ? 'clients' : 'projects'}.
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card variant="bordered" padding="md">
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  Active Projects
                </p>
                <p className="text-2xl font-bold text-obsidian dark:text-signal mt-1">
                  {activeProjects}
                </p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-amber/10 dark:bg-amber/20 flex items-center justify-center">
                <FolderKanban className="w-6 h-6 text-amber" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card variant="bordered" padding="md">
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  {isAdmin ? 'Outstanding' : 'Due'}
                </p>
                <p className="text-2xl font-bold text-obsidian dark:text-signal mt-1">
                  ${(totalPending / 100).toLocaleString()}
                </p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-yellow-100 dark:bg-yellow-900/30 flex items-center justify-center">
                <CreditCard className="w-6 h-6 text-yellow-600 dark:text-yellow-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        {isAdmin ? (
          <>
            <Card variant="bordered" padding="md">
              <CardContent>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600 dark:text-slate-400">
                      New Inquiries
                    </p>
                    <p className="text-2xl font-bold text-obsidian dark:text-signal mt-1">
                      {newInquiries}
                    </p>
                  </div>
                  <div className="w-12 h-12 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                    <MessageSquare className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card variant="bordered" padding="md">
              <CardContent>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600 dark:text-slate-400">
                      Total Clients
                    </p>
                    <p className="text-2xl font-bold text-obsidian dark:text-signal mt-1">
                      {new Set(projects.map((p) => p.client_id)).size}
                    </p>
                  </div>
                  <div className="w-12 h-12 rounded-lg bg-amber/10 dark:bg-amber/20 flex items-center justify-center">
                    <Users className="w-6 h-6 text-amber" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </>
        ) : (
          <>
            <Card variant="bordered" padding="md">
              <CardContent>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600 dark:text-slate-400">
                      Completed
                    </p>
                    <p className="text-2xl font-bold text-obsidian dark:text-signal mt-1">
                      {projects.filter((p) => p.status === 'complete').length}
                    </p>
                  </div>
                  <div className="w-12 h-12 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-green-600 dark:text-green-400" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card variant="bordered" padding="md">
              <CardContent>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600 dark:text-slate-400">
                      Recent Updates
                    </p>
                    <p className="text-2xl font-bold text-obsidian dark:text-signal mt-1">
                      {projects.filter((p) => {
                        const updatedAt = new Date(p.updated_at);
                        const weekAgo = new Date();
                        weekAgo.setDate(weekAgo.getDate() - 7);
                        return updatedAt > weekAgo;
                      }).length}
                    </p>
                  </div>
                  <div className="w-12 h-12 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                    <Clock className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>

      {/* Main Content Grid */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Recent Projects */}
        <Card variant="bordered" padding="none">
          <CardHeader className="p-6 border-b border-slate-200 dark:border-slate-700">
            <div className="flex items-center justify-between">
              <CardTitle>Recent Projects</CardTitle>
              <Link to="/dashboard/projects">
                <Button variant="ghost" size="sm" rightIcon={<ArrowRight className="w-4 h-4" />}>
                  View All
                </Button>
              </Link>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {recentProjects.length === 0 ? (
              <div className="p-6 text-center text-slate-500 dark:text-slate-400">
                No projects yet.
              </div>
            ) : (
              <ul className="divide-y divide-slate-200 dark:divide-slate-700">
                {recentProjects.map((project) => (
                  <li key={project.id}>
                    <Link
                      to={`/dashboard/projects/${project.id}`}
                      className="flex items-center justify-between p-4 hover:bg-slate-50 dark:hover:bg-obsidian-100 transition-colors"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-obsidian dark:text-signal truncate">
                          {project.title}
                        </p>
                        {isAdmin && project.client && (
                          <p className="text-sm text-slate-500 dark:text-slate-400">
                            {project.client.company_name || 'Unknown Client'}
                          </p>
                        )}
                      </div>
                      <Badge variant={statusBadgeVariants[project.status] || 'default'}>
                        {project.status.replace('_', ' ')}
                      </Badge>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        {/* Recent Payments / Inquiries */}
        <Card variant="bordered" padding="none">
          <CardHeader className="p-6 border-b border-slate-200 dark:border-slate-700">
            <div className="flex items-center justify-between">
              <CardTitle>
                {isAdmin ? 'Recent Inquiries' : 'Recent Invoices'}
              </CardTitle>
              <Link to={isAdmin ? '/dashboard/inquiries' : '/dashboard/invoices'}>
                <Button variant="ghost" size="sm" rightIcon={<ArrowRight className="w-4 h-4" />}>
                  View All
                </Button>
              </Link>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {isAdmin ? (
              inquiries.length === 0 ? (
                <div className="p-6 text-center text-slate-500 dark:text-slate-400">
                  No inquiries yet.
                </div>
              ) : (
                <ul className="divide-y divide-slate-200 dark:divide-slate-700">
                  {inquiries.slice(0, 5).map((inquiry) => (
                    <li key={inquiry.id} className="p-4">
                      <div className="flex items-start justify-between gap-4">
                        <div className="min-w-0 flex-1">
                          <p className="font-medium text-obsidian dark:text-signal">
                            {inquiry.name}
                          </p>
                          <p className="text-sm text-slate-500 dark:text-slate-400 truncate">
                            {inquiry.company} · {inquiry.email}
                          </p>
                        </div>
                        <Badge variant={statusBadgeVariants[inquiry.status] || 'default'}>
                          {inquiry.status}
                        </Badge>
                      </div>
                    </li>
                  ))}
                </ul>
              )
            ) : recentPayments.length === 0 ? (
              <div className="p-6 text-center text-slate-500 dark:text-slate-400">
                No invoices yet.
              </div>
            ) : (
              <ul className="divide-y divide-slate-200 dark:divide-slate-700">
                {recentPayments.map((payment) => (
                  <li key={payment.id} className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-obsidian dark:text-signal">
                          ${(payment.amount / 100).toLocaleString()}
                        </p>
                        <p className="text-sm text-slate-500 dark:text-slate-400">
                          {payment.description || 'Invoice'} ·{' '}
                          {format(new Date(payment.created_at), 'MMM d, yyyy')}
                        </p>
                      </div>
                      <Badge variant={statusBadgeVariants[payment.status] || 'default'}>
                        {payment.status}
                      </Badge>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Admin Quick Actions */}
      {isAdmin && (
        <div className="grid sm:grid-cols-3 gap-4">
          <Link to="/dashboard/projects" className="block">
            <Card variant="bordered" padding="md" hover>
              <CardContent>
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-lg bg-amber/10 dark:bg-amber/20 flex items-center justify-center">
                    <FolderKanban className="w-5 h-5 text-amber" />
                  </div>
                  <div>
                    <p className="font-medium text-obsidian dark:text-signal">
                      Create Project
                    </p>
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                      Start a new client project
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>

          <Link to="/dashboard/invoices" className="block">
            <Card variant="bordered" padding="md" hover>
              <CardContent>
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-lg bg-yellow-100 dark:bg-yellow-900/30 flex items-center justify-center">
                    <CreditCard className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
                  </div>
                  <div>
                    <p className="font-medium text-obsidian dark:text-signal">
                      Create Invoice
                    </p>
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                      Bill a client
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>

          <Link to="/dashboard/settings" className="block">
            <Card variant="bordered" padding="md" hover>
              <CardContent>
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-lg bg-amber/10 dark:bg-amber/20 flex items-center justify-center">
                    <Settings className="w-5 h-5 text-amber" />
                  </div>
                  <div>
                    <p className="font-medium text-obsidian dark:text-signal">
                      Settings
                    </p>
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                      Manage your account
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>
        </div>
      )}
    </div>
  );
}

export default DashboardOverview;
